import { once } from '@ember/runloop';
import { service } from '@ember/service';

import type { RecordData, Service } from 'webapp-types';
import { ModelName, ReminderObjectType } from 'webapp/enums/enums';
import type { ValidationRule } from 'webapp/utils/validate';

import BufferedModel from 'webapp/models-custom/buffer/buffered-model';
import type Report from 'webapp/models-custom/report';
import Schedule from 'webapp/models-custom/schedule';
import type ReminderRecord from 'webapp/models/reminder';

import { Severity } from 'webapp/services/logger';

export default class Reminder extends BufferedModel<
	ReminderRecord,
	RecordData.Reminder.Serialized
> {
	@service('logger') declare loggerService: Service.Logger;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;

	// ------- props from / to ED record ------

	get id(): string | null {
		return this.model.id;
	}

	get objectType(): string {
		return this.getBuffered('objectType');
	}

	set objectType(value: string) {
		this.setBuffered('objectType', value);
	}

	get objectId(): number {
		return this.getBuffered('objectId');
	}

	set objectId(value: number) {
		this.setBuffered('objectId', value);
	}

	get scheduleType(): number {
		return this.getBuffered('scheduleType');
	}

	set scheduleType(value: number) {
		this.setBuffered('scheduleType', value);
		this.schedule.type = value;
	}

	get scheduleHourOfDay(): number | null {
		return this.getBuffered('scheduleHourOfDay');
	}

	set scheduleHourOfDay(value: number | null) {
		this.setBuffered('scheduleHourOfDay', value);
		this.schedule.hourOfDay = value;
	}

	get scheduleDayOfWeek(): number[] {
		return this.getBuffered('scheduleDayOfWeek');
	}

	set scheduleDayOfWeek(value: number[]) {
		this.setBuffered('scheduleDayOfWeek', value);
		this.schedule.dayOfWeek = value;
	}

	get scheduleDayOfMonth(): number | null {
		return this.getBuffered('scheduleDayOfMonth');
	}

	set scheduleDayOfMonth(value: number | null) {
		this.setBuffered('scheduleDayOfMonth', value);
		this.schedule.dayOfMonth = value;
	}

	// ------ schedule -------

	declare _schedule: Schedule;

	get schedule(): Schedule {
		if (this._schedule) {
			// mark dependents so this getter updates and triggers the schedule update from record
			// eslint-disable-next-line @typescript-eslint/no-unused-expressions
			this.scheduleType;
			// eslint-disable-next-line @typescript-eslint/no-unused-expressions
			this.scheduleHourOfDay;
			// eslint-disable-next-line @typescript-eslint/no-unused-expressions
			this.scheduleDayOfWeek;
			// eslint-disable-next-line @typescript-eslint/no-unused-expressions
			this.scheduleDayOfMonth;
			once(this._schedule, this._schedule.updateScheduleFromModel, this);
		} else {
			this._schedule = Schedule.createFromModel(this);
		}
		return this._schedule;
	}

	updateScheduleValues(): void {
		const schedule = this.schedule;
		schedule.updateScheduleValues();
		schedule.updateModelFromSchedule(this);
	}

	cleanupUnusedScheduleValues(): void {
		const schedule = this.schedule;
		schedule.cleanupUnusedScheduleValues();
		schedule.updateModelFromSchedule(this);
	}

	// ------- other props ------

	override get validationRules(): Record<string, ValidationRule[]> {
		return {
			objectId: ['required'],
			objectType: ['required'],
			scheduleType: ['required'],
		};
	}

	get isForReport(): boolean {
		return this.objectType === ReminderObjectType.REPORT;
	}

	get report(): Report | null {
		let report;
		if (this.isForReport) {
			const reportId = this.objectId;
			report = this.mmReportsService.findModel(String(reportId));
			if (!report) {
				this.loggerService.logError(
					'Missing report for reminder',
					{
						reminderId: this.id,
						reportId,
					},
					Severity.WARNING,
				);
			}
		}
		return report || null;
	}

	getDataForClone() {
		const data = this.toJSON();
		return data;
	}

	createAnotherModel() {
		return new Reminder({ model: this.model });
	}

	static override modelName = ModelName.REMINDER; // store record type
}
